// =============================================================
// VARIANCE — The Executive Command Center
// File      : AuthContext.tsx
// Location  : /home/Applications/variance/web/src/lib/AuthContext.tsx
// Owner     : VARIANCE frontend application only
// Purpose   : Provides global user session context (roles, identity).
//             Currently hardcoded to Super_Admin. Will be replaced
//             by CogniFleet AUTH (GET /api/auth/me) when ready.
// =============================================================

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// --- TYPES ---
interface AuthContextType {
  user: { id: string; name: string; email: string } | null;
  allowedModules: string[];
  canAccess: (moduleName: string) => boolean;
  isLoading: boolean;
}

// --- CONTEXT DEFINITION ---
const AuthContext = createContext<AuthContextType>({
  user: null, allowedModules: [], canAccess: () => false, isLoading: true
});

// --- PROVIDER COMPONENT ---
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<{ id: string; name: string; email: string } | null>(null);
  const [allowedModules, setAllowedModules] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fetch the current user and their allowed modules
    fetch('/api/auth/me')
      .then(res => res.json())
      .then(data => {
        if (data.user) {
          setUser(data.user);
          setAllowedModules(data.allowed_modules || []);
        }
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  const canAccess = (moduleName: string) => {
    return allowedModules.includes(moduleName);
  };

  return (
    <AuthContext.Provider value={{ user, allowedModules, canAccess, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

// --- CUSTOM HOOK ---
export const useAuth = () => useContext(AuthContext);
